import {
  PROMPT_AUDIO,
  PROMPT_SOURCE_TEXT,
  PROMPT_TRANSLATION_TEXT,
  answerFormatModesFromGranular,
} from '@/utils/testPromptAnswerMatrix'
import type { AnswerFormatMode, PromptTypeKey } from '@/utils/testPromptAnswerMatrix'
import type { WordAttributeFilterKey } from '@/constants/wordAttributeFilterKeys'
import { ATTRIBUTE_META, OPERATORS_BY_TYPE, defaultOperator, newAttributeConditionRow } from './wordAttributeFilters'
import type { AttributeConditionRow, AttributeOperator } from './wordAttributeFilters'
import type { TestCompletionMode } from './testSettingsTypes'
import type { TestTemplate } from '@/types'

/** Условие по атрибуту слова без `id`: идентификатор строки заводит форма. */
export type TemplateAttributeCondition = {
  field: WordAttributeFilterKey
  operator: AttributeOperator
  value: string
}

/**
 * Что шаблон говорит о настройках прогона.
 *
 * Поля необязательные: шаблон переопределяет только то, что в нём записано,
 * остальное форма оставляет как есть. Исключение — `answerFormatModes`: шаблон,
 * заговоривший про ответы, задаёт их набор целиком (в том числе пустой).
 */
export type TemplateSettingsPatch = {
  excludeDuplicateWords?: boolean
  mode?: TestCompletionMode
  countNeed?: number
  durationMin?: number
  seriesTarget?: number
  promptTypes?: PromptTypeKey[]
  answerFormatModes?: AnswerFormatMode[]
  allowHints?: boolean
  allowTranscription?: boolean
  autoCheckEnabled?: boolean
  tagIds?: number[]
  relaxFilters?: boolean
  explicitCardIds?: number[]
  attributeConditions?: TemplateAttributeCondition[]
}

const KNOWN_PROMPT_TYPES: PromptTypeKey[] = [PROMPT_SOURCE_TEXT, PROMPT_TRANSLATION_TEXT, PROMPT_AUDIO]
const KNOWN_MODES: TestCompletionMode[] = ['classic', 'timed', 'series']

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/** Числа в шаблонах приходят и строками: API валидирует `duration_min` как строку. */
function asFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value)

  return null
}

function asBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined
}

export type TemplateReadOptions = {
  /** Отображения, которые предлагает форма: чужие шаблон не навязывает. */
  promptTypes: PromptTypeKey[]
  /** Форматы ответа, которые предлагает форма. */
  answerFormatModes: AnswerFormatMode[]
  /** Атрибуты слова, известные текущему справочнику enum-ов. */
  attributeKeys: WordAttributeFilterKey[]
}

/**
 * Разбор настроек шаблона в патч для формы конструктора.
 *
 * Единственное место, которое знает форматы шаблонов: и учительская форма, и
 * ученическая читают их отсюда, иначе одна научилась бы понимать `duration_min`
 * строкой, а вторая молча оставляла бы поле пустым.
 */
export function readTemplateSettings(tpl: TestTemplate, options: TemplateReadOptions): TemplateSettingsPatch {
  const s = (tpl.settings ?? {}) as Record<string, unknown>
  const completion = asRecord(s.completion_method)
  const help = asRecord(s.help_settings)
  const filters = asRecord(s.filters)

  const patch: TemplateSettingsPatch = {}

  const excludeDuplicateWords = asBoolean(s.exclude_duplicate_words)
  if (excludeDuplicateWords !== undefined) patch.excludeDuplicateWords = excludeDuplicateWords

  // Сначала `completion_method.mode`, затем легаси-поле `mode` — но только если
  // первое действительно назвало режим: иначе шаблон с пустой строкой в
  // `completion_method` затёр бы верное легаси-значение.
  const mode = [completion?.mode, s.mode].find(
    (x): x is TestCompletionMode => typeof x === 'string' && KNOWN_MODES.includes(x as TestCompletionMode),
  )
  if (mode) patch.mode = mode

  const count = asFiniteNumber(s.count_cards) ?? asFiniteNumber(s.count_need)
  if (count !== null) patch.countNeed = count

  const duration = asFiniteNumber(completion?.duration_min ?? s.duration_min)
  if (duration !== null) patch.durationMin = duration

  const series = asFiniteNumber(completion?.series_target ?? s.series_target)
  if (series !== null) patch.seriesTarget = series

  const knownPrompts = (raw: unknown[]): PromptTypeKey[] =>
    raw
      .map((x) => String(x).trim())
      .filter((x): x is PromptTypeKey => KNOWN_PROMPT_TYPES.includes(x as PromptTypeKey))
      .filter((x) => options.promptTypes.includes(x))

  // Массив без единого знакомого отображения — то же самое, что его отсутствие:
  // разбор идёт дальше, к легаси-полю `prompt_type`.
  const prompts = knownPrompts(Array.isArray(s.enabled_prompt_type) ? s.enabled_prompt_type : [])
  const promptsLegacy = typeof s.prompt_type === 'string' ? knownPrompts([s.prompt_type]) : []
  if (prompts.length > 0) patch.promptTypes = prompts
  else if (promptsLegacy.length > 0) patch.promptTypes = promptsLegacy

  // Формат ответа шаблон задаёт целиком: молчащий шаблон обнуляет выбор, иначе
  // от прошлого шаблона остался бы формат, которого плитка не обещала.
  const granular = Array.isArray(s.enabled_answer_type)
    ? s.enabled_answer_type.map((x) => String(x)).filter((x) => x.trim() !== '')
    : typeof s.answer_type === 'string' && s.answer_type.trim() !== ''
      ? [s.answer_type.trim()]
      : []
  patch.answerFormatModes = answerFormatModesFromGranular(granular).filter((m) =>
    options.answerFormatModes.includes(m),
  )

  const allowHints = asBoolean(help?.allow_hints) ?? asBoolean(s.allow_hints)
  if (allowHints !== undefined) patch.allowHints = allowHints

  const allowTranscription = asBoolean(help?.allow_transcription) ?? asBoolean(s.allow_transcription)
  if (allowTranscription !== undefined) patch.allowTranscription = allowTranscription

  const autoCheckEnabled = asBoolean(s.auto_check_enabled)
  if (autoCheckEnabled !== undefined) patch.autoCheckEnabled = autoCheckEnabled

  if (Array.isArray(s.explicit_card_ids)) {
    patch.explicitCardIds = s.explicit_card_ids.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0)
  }

  if (filters) {
    if (Array.isArray(filters.tag_ids)) {
      patch.tagIds = filters.tag_ids.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0)
    }

    const relax = asBoolean(filters.relax_filter) ?? asBoolean(filters.relax_tag_filter)
    if (relax !== undefined) patch.relaxFilters = relax

    if (Array.isArray(filters.attribute_conditions)) {
      patch.attributeConditions = filters.attribute_conditions
        .map((raw) => asRecord(raw))
        .filter((c): c is Record<string, unknown> => c !== null)
        .map((c) => ({
          field: (typeof c.field === 'string' ? c.field : 'part_of_speech') as WordAttributeFilterKey,
          operatorRaw: typeof c.operator === 'string' ? c.operator : '',
          value: typeof c.value === 'string' ? c.value : '',
        }))
        .filter((c) => options.attributeKeys.includes(c.field))
        .map((c) => ({
          field: c.field,
          // Оператор обязан быть из справочника атрибута: чужой Select покажет
          // пустым и отправит на API значение, которого тот не знает.
          operator: OPERATORS_BY_TYPE[ATTRIBUTE_META[c.field].inputType].includes(
            c.operatorRaw as AttributeOperator,
          )
            ? (c.operatorRaw as AttributeOperator)
            : defaultOperator(c.field),
          value: c.value,
        }))
    }
  }

  return patch
}

/** Условия шаблона как строки формы: `id` нужен списку, шаблон его не приносит. */
export function templateConditionsToRows(conditions: TemplateAttributeCondition[]): AttributeConditionRow[] {
  return conditions.map((c) => ({
    ...newAttributeConditionRow(),
    field: c.field,
    operator: c.operator,
    value: c.value,
  }))
}
